/**
 * Lightweight first-party analytics. Writes one event per call into
 * /analyticsEvents. Fully privacy-respecting — no third-party trackers,
 * no IP logging, no fingerprinting. The session id lives in sessionStorage
 * (cleared on tab close) so repeat visitors are NOT correlated across
 * sessions.
 *
 * Schema is enforced server-side in firestore.rules; see the allowlist of
 * event types there.
 */

import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { getDb, isFirebaseConfigured } from '@/lib/firebase';

export type AnalyticsEventType =
  | 'page_view'
  | 'plan_start'
  | 'plan_step'
  | 'plan_complete'
  | 'checkout_start'
  | 'checkout_complete'
  | 'shop_view'
  | 'shop_add_to_cart'
  | 'shop_checkout_start'
  | 'shop_checkout_complete'
  | 'contact_submit'
  | 'cta_click'
  | 'savings_calc_run';

const SESSION_KEY = 'anysharps_analytics_session_v1';
const FIRST_TOUCH_KEY = 'anysharps_analytics_first_touch_v1';

function newSessionId(): string {
  // Time-prefix + random chunk — sortable and collision-safe enough for
  // session-scoped use without pulling uuid.
  const t = Date.now().toString(36);
  const r = Math.random().toString(36).slice(2, 10);
  return `${t}${r}`;
}

function getSessionId(): string {
  if (typeof sessionStorage === 'undefined') return 'no-storage';
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const fresh = newSessionId();
    sessionStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch {
    return 'storage-blocked';
  }
}

/**
 * First-touch data persists for the life of localStorage so we can attribute
 * conversions back to the first referrer/utm. Only set once per browser.
 */
function getFirstTouch(): { referrer: string | null; utm: Record<string, string> } | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const existing = localStorage.getItem(FIRST_TOUCH_KEY);
    if (existing) return JSON.parse(existing) as { referrer: string | null; utm: Record<string, string> };
    const referrer = typeof document !== 'undefined' ? (document.referrer || null) : null;
    const utm: Record<string, string> = {};
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
        const v = params.get(k);
        if (v) utm[k] = v.slice(0, 100);
      }
    }
    const seed = { referrer: referrer ? referrer.slice(0, 500) : null, utm };
    localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(seed));
    return seed;
  } catch {
    return null;
  }
}

function isBot(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /bot|crawl|spider|slurp|bingpreview|headlesschrome|lighthouse/i.test(ua);
}

interface TrackOptions {
  path?: string;
  payload?: Record<string, unknown>;
}

/**
 * Fire-and-forget. Errors are swallowed (analytics must never break the page).
 * Bots and unconfigured Firebase are no-ops.
 */
export function trackEvent(type: AnalyticsEventType, opts: TrackOptions = {}): void {
  if (!isFirebaseConfigured()) return;
  if (isBot()) return;
  void writeEvent(type, opts);
}

async function writeEvent(type: AnalyticsEventType, opts: TrackOptions): Promise<void> {
  try {
    const db = getDb();
    const sessionId = getSessionId();
    const first = getFirstTouch();
    const doc: Record<string, unknown> = {
      type,
      sessionId,
      createdAt: serverTimestamp(),
    };
    if (opts.path) doc.path = opts.path.slice(0, 200);
    if (first?.referrer) doc.referrer = first.referrer;
    if (first?.utm && Object.keys(first.utm).length) doc.utm = first.utm;
    if (opts.payload && Object.keys(opts.payload).length) {
      // Shallow-clone, drop anything that's not primitive/string/number/bool.
      const clean: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(opts.payload)) {
        if (v == null) continue;
        if (typeof v === 'string') clean[k] = v.slice(0, 500);
        else if (typeof v === 'number' || typeof v === 'boolean') clean[k] = v;
        else clean[k] = String(v).slice(0, 500);
      }
      if (Object.keys(clean).length) doc.payload = clean;
    }
    await addDoc(collection(db, 'analyticsEvents'), doc);
  } catch {
    // Silent — never break the page on analytics.
  }
}

/** Convenience for page views with current location. */
export function trackPageView(path: string): void {
  trackEvent('page_view', { path });
}
